import React, { Fragment, useCallback, useEffect, useMemo, useRef, KeyboardEvent } from 'react';
import { ErrorRender } from '../../containers/ErrorRender';
import { RegisterOptions, UseFormReturn, useFormState } from 'react-hook-form';
import { SquareButtonIconsEnum } from '../../../constants/square-button-icons';
import { SquareButton } from '../../actions/SquareButton';
import { FormElementValueType, Void } from '../../../models/basic-objects';
import { idGenerator } from '../../../helpers/idGenerator';
import classNames from 'classnames';
import { InputTypes } from '../../../constants/form';
import { debounce } from 'lodash';
import { Loader } from '../../containers/Loader';
import classes from './Input.module.scss';

// this is needed to provide an interface that tells: hey if you want to make a text area-use both props multiline and rows
// if you paste rows without multiline prop it will give you an error-hint, that you're missing multiline, since rows without multiline is useless
// ( and vice versa )
export type OptionalTextArea =
  | {
      multiline?: false;
      multilineRows?: never;
      multilineResizable?: never;
    }
  | {
      multiline: true;
      multilineRows?: number;
      multilineResizable?: boolean;
    };

export type ActionButtonType = {
  icon: SquareButtonIconsEnum;
  disabled?: boolean;
};

export type InputViewProps = {
  action?: Void;
  actionButton?: ActionButtonType;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  debounceTiming: number;
  disabled?: boolean;
  errorClasses: string;
  executing: boolean;
  formInstance: UseFormReturn;
  globalClasses: string;
  helper?: string;
  helperClass?: string;
  iconClasses: string;
  id?: string;
  labelRightSlot?: React.ReactNode;
  inlinedLabel?: boolean;
  label?: string;
  labelClasses?: string;
  name: string;
  outerClasses: string;
  readonly?: boolean;
  rules: RegisterOptions;
  size: 'large' | 'medium' | 'small';
  type: InputTypes;
  updateValue: (value: FormElementValueType) => void;
  value?: unknown;
  placeholder?: string;
} & OptionalTextArea;

export const InputView = (props: InputViewProps): JSX.Element => {
  const {
    action,
    actionButton,
    onKeyDown,
    disabled,
    errorClasses,
    executing,
    formInstance,
    globalClasses,
    helper,
    helperClass,
    iconClasses,
    id,
    inlinedLabel,
    label,
    labelClasses,
    labelRightSlot,
    multiline,
    multilineRows,
    name,
    outerClasses,
    readonly,
    rules,
    debounceTiming,
    updateValue,
    type,
    value,
    placeholder
  } = props;
  const inputID = useMemo(() => id ?? idGenerator(name), [id, name]);
  const localRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const register = formInstance.register(name, rules);

  useEffect(() => {
    localRef.current && updateValue(localRef.current.value);
  }, [localRef.current?.value, updateValue]);

  useEffect(() => {
    if (localRef.current?.value !== value) {
      updateValue(value as FormElementValueType);
    }
  }, [updateValue, value]);

  const labelRenderer = () => {
    if (!label) return null;

    return (
      <label className={classNames(labelClasses)} htmlFor={inputID}>
        {labelRightSlot ? (
          <div className={'grid-x align-justify'}>
            <div className={'cell shrink'}>{label}</div>
            <div className={'cell shrink'}>{labelRightSlot}</div>
          </div>
        ) : (
          label
        )}
      </label>
    );
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    updateValue(e.target.value);
  };

  const onKeyDownHandler = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (onKeyDown) {
        onKeyDown(e);
      }
    },
    [onKeyDown]
  );

  const inputProps = {
    id: inputID,
    type,
    className: globalClasses,
    disabled,
    onChange: debounceTiming ? debounce(handleChange, debounceTiming) : handleChange,
    readOnly: readonly,
    name,
    onBlur: register.onBlur,
    placeholder,
    ref: (ref: HTMLInputElement | HTMLTextAreaElement | null) => {
      localRef.current = ref;
      register.ref(localRef.current);
    }
  };

  const elem = multiline ? (
    <textarea {...inputProps} rows={multilineRows} />
  ) : (
    <div className={iconClasses}>
      <input {...inputProps} onKeyDown={onKeyDownHandler} />
      {action && actionButton && (
        <SquareButton
          type={'transparent'}
          icon={actionButton.icon}
          disabled={disabled || actionButton.disabled}
          action={action}
        />
      )}
      {executing && (
        <div className={classes.LoaderWrapper}>
          <Loader variation={'dark'} size={'small'} />
        </div>
      )}
    </div>
  );

  const labelElemGroup = (
    <Fragment>
      {labelRenderer()}
      {elem}
    </Fragment>
  );
  // We need to subscribe before using the getFieldState
  const formState = useFormState({ control: formInstance.control });

  const help =
    helper && !formInstance.getFieldState(name, formState).invalid ? <p className={helperClass}>{helper}</p> : null;
  const elemWrapper = inlinedLabel ? (
    <div className={'relative'}>{labelElemGroup}</div>
  ) : (
    <Fragment>{labelElemGroup}</Fragment>
  );

  return (
    <div className={outerClasses}>
      {elemWrapper}
      <ErrorRender errors={formInstance.formState.errors} name={name} className={errorClasses} />
      {help}
    </div>
  );
};
